import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Search, MapPin, Briefcase, Star, Globe, ArrowRight, Loader2, Scale, Send, X, BadgeCheck,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { STATES, CHARGE_TYPES } from "@/data/legalData";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

export default function Attorneys() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [attorneys, setAttorneys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const [state, setState] = useState(searchParams.get("state") || "all");
  const [q, setQ] = useState("");
  const [active, setActive] = useState(null); // attorney for consult
  const [form, setForm] = useState({ name: "", email: "", phone: "", state: "", charge: "", message: "" });
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (state !== "all") params.state = state;
      if (q) params.q = q;
      const { data } = await api.get("/attorneys", { params });
      setAttorneys(data);
    } catch { toast.error("Could not load attorneys"); }
    finally { setLoading(false); }
  }, [state, q]);

  useEffect(() => { load(); }, [load]);

  const openConsult = (a) => {
    if (!user) { navigate("/login"); return; }
    setForm({ name: user.name || "", email: user.email || "", phone: "", state: a.states?.[0] || "", charge: "", message: "" });
    setActive(a);
  };

  const submitConsult = async () => {
    if (!form.name || !form.email) { toast.error("Please add your name and email"); return; }
    setSending(true);
    try {
      await api.post(`/attorneys/${active.attorney_id}/consult`, form);
      toast.success("Your request was sent to " + active.name);
      setActive(null);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send request"); }
    finally { setSending(false); }
  };

  return (
    <div className="grain-bg min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
          <div className="max-w-2xl">
            <span className="font-mono text-xs uppercase tracking-[0.18em] text-terracotta">Attorney Marketplace</span>
            <h1 className="mt-3 font-serif text-4xl sm:text-5xl font-semibold text-forest-900">Find a criminal defense attorney</h1>
            <p className="mt-4 text-ink-700 leading-relaxed">Browse vetted defense attorneys by state and practice area, then request a free consultation. Requesting a consult is always free for you.</p>
          </div>
          <Link to="/attorneys/join" data-testid="attorney-join-cta">
            <Button variant="outline" className="rounded-full border-forest-800/20 text-forest-800 gap-2"><Briefcase className="h-4 w-4" /> List your practice</Button>
          </Link>
        </div>

        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-400" />
            <Input data-testid="attorney-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, firm, or practice area…" className="pl-11 rounded-full border-parchment-200 bg-white h-11" />
          </div>
          <Select value={state} onValueChange={setState}>
            <SelectTrigger data-testid="attorney-state-filter" className="rounded-full border-parchment-200 bg-white h-11 sm:w-64"><SelectValue placeholder="All states" /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all">All states</SelectItem>
              {STATES.map((s) => <SelectItem key={s.abbr} value={s.name}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="mt-16 grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-forest-700" /></div>
        ) : attorneys.length === 0 ? (
          <p className="mt-12 text-center text-ink-500">No attorneys match your search yet. Try a different state or clear the filters.</p>
        ) : (
          <div className="mt-8 grid md:grid-cols-2 gap-5">
            {attorneys.map((a) => (
              <motion.div key={a.attorney_id} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4 }}
                data-testid={`attorney-card-${a.attorney_id}`} className={`rounded-2xl border bg-white p-6 ${a.featured ? "border-terracotta/40 ring-1 ring-terracotta/10" : "border-parchment-200"}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <span className="grid place-items-center h-12 w-12 rounded-xl bg-forest-800 text-parchment-50 font-serif text-lg">{a.name.split(" ").map((n) => n[0]).slice(0, 2).join("")}</span>
                    <div>
                      <h3 className="font-semibold text-forest-900 text-lg leading-tight">{a.name}</h3>
                      {a.firm && <p className="text-sm text-ink-500">{a.firm}</p>}
                    </div>
                  </div>
                  {a.verified && <span className="inline-flex items-center gap-1 rounded-full bg-forest-800 text-parchment-50 text-[10px] font-semibold px-2.5 py-1"><BadgeCheck className="h-3 w-3" /> Verified</span>}
                  {!a.verified && a.featured && <span className="inline-flex items-center gap-1 rounded-full bg-terracotta-subtle text-terracotta text-[10px] font-semibold px-2.5 py-1"><Star className="h-3 w-3" /> Featured</span>}
                </div>

                <div className="mt-4 flex flex-wrap gap-1.5">
                  {(a.states || []).map((s) => <span key={s} className="inline-flex items-center gap-1 rounded-full bg-forest-100 text-forest-800 text-xs px-2.5 py-1"><MapPin className="h-3 w-3" />{s}</span>)}
                </div>
                {a.bio && <p className="mt-3 text-sm text-ink-700 leading-relaxed line-clamp-3">{a.bio}</p>}
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {(a.practice_areas || []).slice(0, a.featured ? 12 : 3).map((p) => <span key={p} className="rounded-full bg-parchment-100 text-ink-600 text-xs px-2.5 py-1">{p}</span>)}
                </div>

                <div className="mt-5 flex items-center justify-between">
                  <span className="text-xs text-ink-400">{a.years_experience ? `${a.years_experience} yrs experience` : ""}</span>
                  <div className="flex gap-2">
                    {a.website && <a href={a.website} target="_blank" rel="noreferrer"><Button variant="ghost" size="sm" className="text-forest-700 gap-1"><Globe className="h-4 w-4" /> Site</Button></a>}
                    <Button onClick={() => openConsult(a)} data-testid={`consult-btn-${a.attorney_id}`} className="rounded-full bg-forest-800 hover:bg-forest-900 text-parchment-50 gap-1.5">Request consult <ArrowRight className="h-4 w-4" /></Button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-lg bg-parchment-50 border-parchment-200">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-forest-900 flex items-center gap-2"><Scale className="h-5 w-5 text-forest-700" /> Request a consult</DialogTitle>
          </DialogHeader>
          {active && <p className="text-sm text-ink-600 -mt-2">with <strong>{active.name}</strong>{active.firm ? ` · ${active.firm}` : ""}. This is free — the attorney will reach out to you.</p>}
          <div className="space-y-3 mt-2">
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label className="text-ink-700 mb-1 block">Your name</Label><Input data-testid="consult-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-xl border-parchment-200 bg-white" /></div>
              <div><Label className="text-ink-700 mb-1 block">Email</Label><Input data-testid="consult-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-xl border-parchment-200 bg-white" /></div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label className="text-ink-700 mb-1 block">Phone (optional)</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rounded-xl border-parchment-200 bg-white" /></div>
              <div><Label className="text-ink-700 mb-1 block">Charge (optional)</Label>
                <Select value={form.charge} onValueChange={(v) => setForm({ ...form, charge: v })}>
                  <SelectTrigger className="rounded-xl border-parchment-200 bg-white"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{CHARGE_TYPES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="text-ink-700 mb-1 block">What do you need help with?</Label><Textarea data-testid="consult-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={3} className="rounded-xl border-parchment-200 bg-white resize-none" placeholder="Briefly describe your situation (don't share sensitive details here)." /></div>
          </div>
          <DialogFooter className="mt-3">
            <Button variant="outline" onClick={() => setActive(null)} className="rounded-xl border-parchment-200">Cancel</Button>
            <Button onClick={submitConsult} data-testid="consult-submit" disabled={sending} className="rounded-xl bg-terracotta hover:bg-terracotta-hover text-white gap-2">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
